/**
 * OutbreakGrid — sortable virtualised table of regions for the active outbreak.
 *
 * Data source: `regions.list({ slug })` via hand-rolled GET (same pattern as
 * NewsFeed — no @trpc/client runtime overhead).
 *
 * Per-country case counts (confirmed / deaths / CFR / verifiedAt) are joined
 * server-side from the latest `country_status` row per `countryCode` in the
 * regions.list tRPC procedure. Country display name is derived locally via
 * Intl.DisplayNames from `countryCode`. The Trend column remains "—" until a
 * time-series query is wired in a follow-up.
 *
 * Rules consulted:
 *   - .claude/rules/rtl-support-arabic.md   (logical CSS, no physical margins)
 *   - .claude/rules/uncodixify-ui.md        (no lift, no shadow escalation)
 *   - .claude/rules/accessibility-and-content-provenance.md (role=grid, aria-sort)
 */

// @vitest-environment jsdom — set in test file, not here

import { useQuery } from "@tanstack/react-query";
import {
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useMemo, useRef, useState } from "react";

import type { RegionListItem } from "@hantavirus/shared";
import { type TranslateFn, formatDate, formatNumber } from "#/lib/i18n.js";

// ------------------------------------------------------------------
// Props
// ------------------------------------------------------------------

export interface OutbreakGridProps {
  slug: string;
  locale: string;
  t: TranslateFn;
  initialItems?: RegionListItem[];
  /** Override for API base URL — used in tests and SSR callers. */
  apiBaseUrl?: string;
}

// ------------------------------------------------------------------
// Data fetching (hand-rolled GET, same wire format as NewsFeed)
// ------------------------------------------------------------------

function buildApiUrl(base: string | undefined): string {
  const fallback =
    typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined;
  return `${(base ?? fallback ?? "").replace(/\/+$/u, "")}/api/trpc/regions.list`;
}

async function fetchRegions(
  slug: string,
  locale: string,
  apiBaseUrl?: string,
): Promise<RegionListItem[]> {
  const encoded = encodeURIComponent(JSON.stringify({ json: { slug } }));
  const url = `${buildApiUrl(apiBaseUrl)}?input=${encoded}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { "x-locale": locale, accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`regions.list failed: ${res.status}`);
  const json = (await res.json()) as {
    result?: { data?: { json?: RegionListItem[] } | RegionListItem[] };
  };
  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RegionListItem[] }).json
      : (inner as RegionListItem[] | undefined);
  if (!data) throw new Error("regions.list: malformed response");
  return data.map((item) => ({
    ...item,
    verifiedAt: item.verifiedAt ? new Date(item.verifiedAt as unknown as string) : null,
  }));
}

// ------------------------------------------------------------------
// Inline SVG sort indicators (no icon-library dependency)
// ------------------------------------------------------------------

function SortAscIcon() {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      style={{ flexShrink: 0 }}
    >
      <path d="M5 1L1 9h8L5 1z" />
    </svg>
  );
}

function SortDescIcon() {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      style={{ flexShrink: 0 }}
    >
      <path d="M5 9L1 1h8L5 9z" />
    </svg>
  );
}

function SortNeutralIcon() {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      style={{ flexShrink: 0, opacity: 0.35 }}
    >
      <path d="M5 1L1 5h8L5 1zM5 9L1 5h8L5 9z" />
    </svg>
  );
}

// ------------------------------------------------------------------
// Skeleton cell
// ------------------------------------------------------------------

function SkeletonCell({ width = "5rem" }: { width?: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        height: "0.875rem",
        width,
        background: "var(--color-border-subtle, #e5e7eb)",
        borderRadius: "0.25rem",
      }}
    />
  );
}

// ------------------------------------------------------------------
// Country name resolver (locale-aware, falls back to ISO code)
// ------------------------------------------------------------------

function makeCountryNameResolver(locale: string): (code: string | null) => string {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames([locale, "en"], { type: "region" });
  } catch {
    dn = null;
  }
  return (code: string | null): string => {
    if (!code) return "—";
    const upper = code.toUpperCase();
    if (dn) {
      try {
        const name = dn.of(upper);
        if (name && name !== upper) return name;
      } catch {
        /* fall through to raw code */
      }
    }
    return upper;
  };
}

// ------------------------------------------------------------------
// Column helper + column definitions
// ------------------------------------------------------------------

const columnHelper = createColumnHelper<RegionListItem>();

const MutedDash = <span style={{ color: "var(--color-fg-muted, #6b7280)" }}>—</span>;

function buildColumns(t: TranslateFn, locale: string) {
  const countryName = makeCountryNameResolver(locale);
  return [
    columnHelper.accessor((row) => row.name || row.dataText || String(row.id), {
      id: "name",
      header: () => t("grid.column.region"),
      enableSorting: true,
      cell: (info) => <span style={{ fontWeight: 500 }}>{info.getValue()}</span>,
    }),
    // Country — derived from countryCode via Intl.DisplayNames
    columnHelper.accessor("countryCode", {
      id: "country",
      header: () => t("grid.column.country"),
      enableSorting: true,
      cell: (info) => {
        const code = info.getValue();
        if (!code) return MutedDash;
        return <span>{countryName(code)}</span>;
      },
    }),
    // Confirmed — joined casesConfirmed from country_status (latest per code).
    // Signal mentions are intentionally not used as a numeric fallback here:
    // they are media/source mentions, not case totals.
    columnHelper.accessor(
      (row) => row.casesConfirmed,
      {
        id: "confirmed",
        header: () => t("grid.column.confirmed"),
        enableSorting: true,
        sortDescFirst: true,
        sortUndefined: "last",
        cell: (info) => {
          const row = info.row.original;
          const confirmed = row.casesConfirmed;
          const value = confirmed;
          if (value === null || value === undefined || value === 0) {
            return MutedDash;
          }
          return (
            <span
              style={{
                fontFamily: "var(--font-mono, monospace)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatNumber(value, locale)}
            </span>
          );
        },
      },
    ),
    // CFR — computed server-side as deaths / casesConfirmed * 100
    columnHelper.accessor("caseFatalityPct", {
      id: "cfr",
      header: () => t("grid.column.cfr"),
      enableSorting: true,
      sortDescFirst: true,
      sortUndefined: "last",
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined) return MutedDash;
        return (
          <span
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {`${formatNumber(v, locale)} %`}
          </span>
        );
      },
    }),
    // Last updated — shows verifiedAt when present, stub "—" when null
    columnHelper.accessor("verifiedAt", {
      id: "lastUpdated",
      header: () => t("grid.column.lastUpdated"),
      enableSorting: false,
      cell: (info) => {
        const v = info.getValue();
        if (!v) return <span style={{ color: "var(--color-fg-muted, #6b7280)" }}>—</span>;
        const dateObj = v instanceof Date ? v : new Date(v as unknown as string);
        return (
          <time
            dateTime={dateObj.toISOString()}
            style={{ fontSize: "0.8125rem", fontFamily: "var(--font-mono, monospace)" }}
          >
            {formatDate(dateObj, locale, { dateStyle: "medium" })}
          </time>
        );
      },
    }),
    // Trend — approved placeholder per implementation plan
    columnHelper.display({
      id: "trend",
      header: () => t("grid.column.trend"),
      enableSorting: false,
      cell: () => <span aria-hidden="true">—</span>,
    }),
  ];
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export default function OutbreakGrid({
  slug,
  locale,
  t,
  initialItems,
  apiBaseUrl,
}: OutbreakGridProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "confirmed", desc: true }]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useQuery<RegionListItem[], Error>({
    queryKey: ["regions", "list", slug],
    queryFn: () => fetchRegions(slug, locale, apiBaseUrl),
    initialData: initialItems,
    staleTime: 60_000,
  });

  const rows = data ?? [];

  const columns = useMemo(() => buildColumns(t, locale), [t, locale]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableRows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 48,
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const paddingTop = virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0) : 0;

  const getSortIcon = useCallback(
    (colId: string) => {
      const s = sorting.find((x) => x.id === colId);
      if (!s) return <SortNeutralIcon />;
      return s.desc ? <SortDescIcon /> : <SortAscIcon />;
    },
    [sorting],
  );

  const ariaSort = (isSorted: false | "asc" | "desc"): "ascending" | "descending" | "none" => {
    if (isSorted === "asc") return "ascending";
    if (isSorted === "desc") return "descending";
    return "none";
  };

  // -------------------------------------------------------------------
  // Empty state (not loading)
  // -------------------------------------------------------------------
  if (!isLoading && rows.length === 0) {
    return (
      <section
        data-testid="outbreak-grid"
        style={{
          padding: "2rem 1.5rem",
          textAlign: "center",
          color: "var(--color-fg-muted, #6b7280)",
          fontSize: "0.9375rem",
        }}
      >
        {t("grid.empty")}
      </section>
    );
  }

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------
  return (
    <section
      data-testid="outbreak-grid"
      style={{ paddingInline: "clamp(0rem, 4vw, 1.5rem)", paddingBlock: "1.5rem" }}
    >
      <div className="grid gap-3 sm:hidden">
        {tableRows.map((row) => {
          const item = row.original;
          const countryName = makeCountryNameResolver(locale)(item.countryCode);
          const confirmed = item.casesConfirmed;
          const verifiedAt = item.verifiedAt
            ? item.verifiedAt instanceof Date
              ? item.verifiedAt
              : new Date(item.verifiedAt as unknown as string)
            : null;

          return (
            <article
              key={row.id}
              className="rounded-lg border border-[color:var(--color-border-default)] bg-[color:var(--color-bg-surface)] p-4"
            >
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-[color:var(--color-fg-default)]">
                  {item.name || item.dataText || String(item.id)}
                </h3>
                <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">{countryName}</p>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs uppercase text-[color:var(--color-fg-subtle)]">
                    {t("grid.column.confirmed")}
                  </dt>
                  <dd className="mt-1 font-mono text-[color:var(--color-fg-default)]">
                    {confirmed ? formatNumber(confirmed, locale) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-[color:var(--color-fg-subtle)]">
                    {t("grid.column.cfr")}
                  </dt>
                  <dd className="mt-1 font-mono text-[color:var(--color-fg-default)]">
                    {item.caseFatalityPct == null
                      ? "—"
                      : `${formatNumber(item.caseFatalityPct, locale)} %`}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs uppercase text-[color:var(--color-fg-subtle)]">
                    {t("grid.column.lastUpdated")}
                  </dt>
                  <dd className="mt-1 text-[color:var(--color-fg-default)]">
                    {verifiedAt ? formatDate(verifiedAt, locale, { dateStyle: "medium" }) : "—"}
                  </dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
      <div
        ref={scrollRef}
        className="hidden sm:block"
        style={{
          maxHeight: "640px",
          overflow: "auto",
          maxWidth: "100%",
          border: "1px solid var(--color-border-default, #e5e7eb)",
          borderRadius: "0.5rem",
        }}
      >
        <table
          role="grid"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.875rem",
          }}
        >
          {/* ---- Header ---- */}
          <thead
            style={{
              background: "var(--color-bg-surface, #fff)",
              borderBottom: "1px solid var(--color-border-default, #e5e7eb)",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const toggle = header.column.getToggleSortingHandler();
                  const colLabel = flexRender(header.column.columnDef.header, header.getContext());
                  const isEndAligned =
                    header.column.id === "confirmed" ||
                    header.column.id === "cfr" ||
                    header.column.id === "lastUpdated" ||
                    header.column.id === "trend";

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={canSort ? ariaSort(isSorted) : undefined}
                      style={{
                        padding: 0,
                        textAlign: isEndAligned ? "end" : "start",
                        fontWeight: 600,
                        fontSize: "0.6875rem",
                        color: "var(--color-fg-muted, #6b7280)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={toggle}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggle?.(e as unknown as MouseEvent);
                            }
                          }}
                          aria-label={t("grid.sortBy", {
                            column: String(
                              header.column.columnDef.header instanceof Function
                                ? header.column.columnDef.header(header.getContext())
                                : header.column.columnDef.header,
                            ),
                          })}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            padding: "0.75rem",
                            minHeight: "44px",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "inherit",
                            font: "inherit",
                            fontWeight: "inherit",
                            fontSize: "inherit",
                            textTransform: "inherit",
                            letterSpacing: "inherit",
                            width: "100%",
                            justifyContent: isEndAligned ? "flex-end" : "flex-start",
                          }}
                        >
                          {colLabel}
                          {getSortIcon(header.column.id)}
                        </button>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "0.75rem",
                            minHeight: "44px",
                            width: "100%",
                            justifyContent: isEndAligned ? "flex-end" : "flex-start",
                          }}
                        >
                          {colLabel}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* ---- Body ---- */}
          <tbody>
            {isLoading ? (
              /* Loading skeleton — 8 rows */
              Array.from({ length: 8 }, (_, i) => (
                <tr
                  key={`skeleton-${i}`}
                  style={{
                    borderBottom: "1px solid var(--color-border-subtle, #f3f4f6)",
                    height: "48px",
                  }}
                >
                  {columns.map((col) => (
                    <td key={col.id} style={{ padding: "0.75rem", verticalAlign: "middle" }}>
                      <SkeletonCell />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <>
                {/* Top padding spacer */}
                {paddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={columns.length}
                      style={{ height: `${paddingTop}px`, padding: 0 }}
                    />
                  </tr>
                )}

                {virtualItems.map((vRow) => {
                  const row = tableRows[vRow.index];
                  if (!row) return null;
                  return (
                    <tr
                      key={row.id}
                      style={{
                        height: `${vRow.size}px`,
                        borderBottom: "1px solid var(--color-border-subtle, #f3f4f6)",
                        transition: "background-color 150ms",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                          "var(--color-bg-hover, #f9fafb)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = "";
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isEndAligned =
                          cell.column.id === "confirmed" ||
                          cell.column.id === "cfr" ||
                          cell.column.id === "lastUpdated" ||
                          cell.column.id === "trend";
                        return (
                          <td
                            key={cell.id}
                            style={{
                              padding: "0 0.75rem",
                              textAlign: isEndAligned ? "end" : "start",
                              color: "var(--color-fg-default, #111827)",
                              verticalAlign: "middle",
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* Bottom padding spacer */}
                {paddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={columns.length}
                      style={{ height: `${paddingBottom}px`, padding: 0 }}
                    />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
